import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { adminAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { AdminKpiGrid } from '../components/admin/AdminKpiGrid';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminAuditTab } from '../components/admin/AdminAuditTab';
import { AdminChartsTab } from '../components/admin/AdminChartsTab';
import { ScoringAccuracyTab } from '../components/admin/ScoringAccuracyTab';
import { AiUsageTab } from '../components/admin/AiUsageTab';
import { AiUsageByLoginTab } from '../components/admin/AiUsageByLoginTab';
import { AiCostPerHireTab } from '../components/admin/AiCostPerHireTab';
import { LearningTab } from '../components/admin/LearningTab';
import { AdminDomainRulesTab } from '../components/admin/AdminDomainRulesTab';
import { AdminInvoicesTab } from '../components/admin/AdminInvoicesTab';
import { SecurityCheckTab } from '../components/admin/SecurityCheckTab';
import { AdminUserDialog } from '../components/admin/AdminUserDialog';
import { AdminCreateUserDialog } from '../components/admin/AdminCreateUserDialog';
import { Button } from '../components/ui/button';
import { UserPlus } from 'lucide-react';

export default function AdminDashboardPage() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin' || !!currentUser?.is_super_admin;
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [scoringAccuracy, setScoringAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [sRes, uRes, aRes, accRes] = await Promise.all([
        adminAPI.getStats(), adminAPI.getUsers(), adminAPI.getAuditLogs(), adminAPI.getScoringAccuracy()
      ]);
      setStats(sRes.data);
      setUsers(uRes.data);
      setAuditLogs(aRes.data);
      setScoringAccuracy(accRes.data);
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSelectUser = useCallback((u) => { setSelectedUser(u); setEditRole(u.role); }, []);

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    try {
      await adminAPI.updateUser(selectedUser._id, { role: editRole });
      toast.success(`Role updated to ${editRole}`);
      setSelectedUser(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user ${name}? This cannot be undone.`)) return;
    try {
      await adminAPI.deleteUser(userId);
      toast.success('User deleted');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const pieData = useMemo(() => [
    { name: 'Recruiters', value: stats?.total_recruiters || 0, color: '#0076A3' },
    { name: 'Candidates', value: stats?.total_candidate_users || 0, color: '#059669' },
    { name: 'Admins', value: (stats?.total_users || 0) - (stats?.total_recruiters || 0) - (stats?.total_candidate_users || 0), color: '#D97706' },
  ].filter(d => d.value > 0), [stats]);

  const activityData = useMemo(() => [
    { name: 'Jobs', value: stats?.total_jobs || 0 },
    { name: 'Resumes', value: stats?.total_resumes || 0 },
    { name: 'Assessments', value: stats?.total_assessments || 0 },
    { name: 'Interviews', value: stats?.total_interview_sessions || 0 },
  ], [stats]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#64748B' }}>Loading admin panel...</div>;

  return (
    <div data-testid="admin-dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Platform overview, user management, and audit trail</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="open-create-user-btn" style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600 }}>
          <UserPlus size={16} className="mr-2" /> Create User
        </Button>
      </div>

      <AdminKpiGrid stats={stats} />

      <Tabs defaultValue="users">
        <TabsList style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 10, marginBottom: '1.25rem' }}>
          <TabsTrigger value="users" style={{ borderRadius: 8 }}>Users ({users.length})</TabsTrigger>
          <TabsTrigger value="audit" style={{ borderRadius: 8 }}>Audit Logs ({auditLogs.length})</TabsTrigger>
          <TabsTrigger value="domains" style={{ borderRadius: 8 }}>Domain Rules</TabsTrigger>
          <TabsTrigger value="accuracy" style={{ borderRadius: 8 }} data-testid="tab-scoring-accuracy">Scoring Accuracy</TabsTrigger>
          <TabsTrigger value="ai-usage" style={{ borderRadius: 8 }} data-testid="tab-ai-usage">AI Usage</TabsTrigger>
          <TabsTrigger value="ai-usage-login" style={{ borderRadius: 8 }} data-testid="tab-ai-usage-login">Usage by Login</TabsTrigger>
          <TabsTrigger value="ai-cost-hire" style={{ borderRadius: 8 }} data-testid="tab-ai-cost-hire">Cost / Hire</TabsTrigger>
          <TabsTrigger value="learning" style={{ borderRadius: 8 }} data-testid="tab-learning">Learning</TabsTrigger>
          <TabsTrigger value="invoices" style={{ borderRadius: 8 }} data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="charts" style={{ borderRadius: 8 }}>Charts</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="security" style={{ borderRadius: 8 }} data-testid="tab-security">Security</TabsTrigger>}
        </TabsList>

        <TabsContent value="users">
          <AdminUsersTab users={users} onSelect={handleSelectUser} onDelete={handleDelete} />
        </TabsContent>

        <TabsContent value="audit">
          <AdminAuditTab auditLogs={auditLogs} />
        </TabsContent>

        <TabsContent value="domains">
          <AdminDomainRulesTab />
        </TabsContent>

        <TabsContent value="accuracy">
          <ScoringAccuracyTab data={scoringAccuracy} />
        </TabsContent>

        <TabsContent value="ai-usage">
          <AiUsageTab />
        </TabsContent>

        <TabsContent value="ai-usage-login">
          <AiUsageByLoginTab />
        </TabsContent>

        <TabsContent value="ai-cost-hire">
          <AiCostPerHireTab />
        </TabsContent>

        <TabsContent value="learning">
          <LearningTab />
        </TabsContent>

        <TabsContent value="invoices">
          <AdminInvoicesTab />
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="security">
            <SecurityCheckTab />
          </TabsContent>
        )}

        <TabsContent value="charts">
          <AdminChartsTab activityData={activityData} pieData={pieData} />
        </TabsContent>
      </Tabs>

      <AdminUserDialog
        selectedUser={selectedUser}
        currentUser={currentUser}
        editRole={editRole}
        setEditRole={setEditRole}
        onClose={() => setSelectedUser(null)}
        onSave={handleUpdateRole}
      />

      <AdminCreateUserDialog
        open={showCreate}
        currentUser={currentUser}
        onClose={() => setShowCreate(false)}
        onCreated={loadData}
      />
    </div>
  );
}
